import { ProgressCircleVariant } from '@repo/themes/styled-system/recipes';
import { createContext, ReactNode, useContext, useMemo } from 'react';

interface ProgressCircleContextValue {
  percentage?: number;
  variant?: ProgressCircleVariant['variant'];
}

const ProgressCircleContext = createContext<
  ProgressCircleContextValue | undefined
>(undefined);

export const useProgressCircle = () => {
  const context = useContext(ProgressCircleContext);
  if (!context) {
    throw new Error(
      'useProgressCircle must be used within a ProgressCircleProvider',
    );
  }
  return context;
};

export const getValue = (value: number, max: number) => {
  return Math.max(0, Math.min(value, max));
};
export interface ProgressCircleProviderProps {
  children: ReactNode;
  value: number;
  max: number;
}

export function ProgressCircleProvider({
  children,
  value,
  max,
}: ProgressCircleProviderProps) {
  const percentage = useMemo(() => {
    if (!max || max <= 0) return 0;
    return (getValue(value, max) / max) * 100;
  }, [value, max]);

  const getVariant = (): ProgressCircleVariant['variant'] => {
    if (max === 0) return 'dimmed';
    if (percentage >= 75) return 'red';
    if (percentage >= 50) return 'warning';
    if (percentage >= 25) return 'orange';
    return 'green';
  };

  const providerValues = {
    percentage,
    variant: getVariant(),
  };

  return (
    <ProgressCircleContext.Provider value={providerValues}>
      {children}
    </ProgressCircleContext.Provider>
  );
}
